import React, { useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import './AXPCAssistant.scss';
import { Button } from "devextreme-react/button";
import TextBox from 'devextreme-react/text-box';
import ScrollView from 'devextreme-react/scroll-view';
import AXPCAssistantMessage from './AXPCAssistantMessage';
import TypingIndicator from 'devextreme/esm/__internal/ui/chat/typingindicator';
import notify from 'devextreme/ui/notify';

export default function AXPCAssistant({
  messages,
  currentUser,
  typingUsers,
  onMessageEntered,
  suggestions,
}) {
  const scrollViewRef = useRef(null);
  const fileInputRef = useRef(null);
  const [currentMessage, setCurrentMessage] = useState('');
  const typingIndicatorRef = useRef(null);
  const [renderedMessages, setRenderedMessages] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const onSuggestionClick = useCallback((suggestion) => {
    setCurrentMessage(suggestion);
  }, []);

  useLayoutEffect(() => {
    if (scrollViewRef.current) {
      const scrollable = scrollViewRef.current.instance();
      // Update the scrollable component to recalculate its dimensions
      scrollable.update();
      // Scroll to the bottom
      scrollable.scrollTo(scrollable.scrollHeight());
    }
  }, [renderedMessages]);

  useEffect(() => {
    setRenderedMessages(
      messages.map((message, index) => (
        <AXPCAssistantMessage
          key={message.id || `${message.author.id}-${message.timestamp.getTime()}-${index}`}
          messageData={message}
          isFirstMessage={messages.length > 0 && message.id === messages[0].id}
          suggestions={suggestions}
          onSuggestionClick={onSuggestionClick}
        />
      ))
    )
  }, [messages, onSuggestionClick, suggestions]);

  useEffect(() => {
    if (typingIndicatorRef.current) {
      const instance = new TypingIndicator(typingIndicatorRef.current, {
        typingUsers: typingUsers,
      });
      return () => instance.dispose();
    }
  }, [typingUsers]);

  const handleSendMessage = () => {
    if (currentMessage.trim() || attachedFiles.length > 0) {
      onMessageEntered({
        message: { text: currentMessage },
        attachments: attachedFiles
      });
      setCurrentMessage('');
      setAttachedFiles([]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.event.key === 'Enter' && !e.event.shiftKey) {
      e.event.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      // Validate file size (max 4.5 MB)
      if (file.size > 4.5 * 1024 * 1024) {
        notify(`File "${file.name}" is too large. Maximum size is 4.5 MB.`, 'error', 5000);
        return;
      }
      
      const format = file.name.split('.').pop().toLowerCase();
      const allowedFormats = ['pdf', 'docx', 'csv', 'xlsx', 'txt', 'html'];
      if (!allowedFormats.includes(format)) {
        notify(`Unsupported format ".${format}". Supported: ${allowedFormats.join(', ')}`, 'error', 5000);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result.split(',')[1];
        setAttachedFiles(prev => [...prev, {
          name: file.name,
          format: format,
          size: file.size,
          base64: base64String
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset file input value
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="axpc-assistant-chat-container">
      <ScrollView id="axpc-assistant-scrollview"
        className="axpc-assistant-messages-container"
        ref={scrollViewRef}
        height="calc(100% - 5px)"
        direction="vertical">
        <div className="axpc-assistant-messages-list">
          {renderedMessages}
          {typingUsers.length > 0 && <div ref={typingIndicatorRef} />}
        </div>
      </ScrollView>
      <div className="axpc-assistant-input-container">
        {attachedFiles.length > 0 && (
          <div className="attached-files-preview d-flex flex-row flex-wrap gap-2 mb-2 px-2">
            {attachedFiles.map((file, idx) => (
              <div key={idx} className="file-chip d-flex align-items-center">
                <i className={`fa ${file.format === 'pdf' ? 'fa-file-pdf-o text-danger' : 'fa-file-word-o text-primary'} mr-1`} />
                <span className="file-name">{file.name}</span>
                <button className="remove-file-btn" onClick={() => handleRemoveFile(idx)}>
                  <i className="fa fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="d-flex flex-row align-items-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.docx,.csv,.xlsx,.txt,.html"
            multiple
          />
          <Button
            icon="fa fa-paperclip"
            onClick={handleAttachClick}
            stylingMode="text"
            className="attach-btn"
          />
          <TextBox
            value={currentMessage}
            width="100%"
            onValueChanged={(e) => setCurrentMessage(e.value)}
            valueChangeEvent="keyup"
            placeholder="Type your message..."
            onKeyDown={handleKeyDown}
          />
          <Button
            icon="fa fa-paper-plane"
            onClick={handleSendMessage}
            stylingMode="text"
            disabled={!currentMessage.trim() && attachedFiles.length === 0}
          />
        </div>
      </div>
    </div>
  );
}